import type { ReactNode } from "react";
import { creamTokens } from "../../design-system/creamDesignSystem";
import { Icon, type IconName } from "../ui/Icon";

const c = creamTokens.color;

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
      <div style={{ minWidth: 0, maxWidth: 760 }}>
        {eyebrow && <div style={{ fontSize: 11, fontWeight: 900, color: c.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{eyebrow}</div>}
        <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.12, color: c.rose, fontFamily: creamTokens.font.heading, fontWeight: 900 }}>{title}</h1>
        {subtitle && <p style={{ margin: "7px 0 0", color: c.muted, fontSize: 13, lineHeight: 1.55 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{actions}</div>}
    </header>
  );
}

export function MetricCard({ label, value, detail, tone = c.rose, icon }: { label: string; value: ReactNode; detail?: ReactNode; tone?: string; icon: IconName }) {
  return (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderTop: `3px solid ${tone}`, borderRadius: creamTokens.radius.card, padding: 18, boxShadow: creamTokens.shadow.card, minHeight: 112 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div style={{ fontSize: 10, fontWeight: 900, color: c.muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
        <span style={{ color: tone, display: "inline-flex" }}><Icon name={icon} size={21} /></span>
      </div>
      <div style={{ marginTop: 8, color: tone, fontSize: 28, lineHeight: 1, fontWeight: 900, fontFamily: creamTokens.font.heading }}>{value}</div>
      {detail && <div style={{ marginTop: 6, color: c.muted, fontSize: 11, lineHeight: 1.45 }}>{detail}</div>}
    </div>
  );
}

export function AlertCard({ tone = "warning", title, children }: { tone?: "warning" | "danger" | "info" | "success"; title: string; children?: ReactNode }) {
  const map = {
    warning: { fg: c.warning, bg: "#FFF8E6", icon: "triangle-alert" as IconName },
    danger: { fg: c.critical, bg: "#FDEAEA", icon: "triangle-alert" as IconName },
    info: { fg: c.info, bg: "#E8F0FB", icon: "help-circle" as IconName },
    success: { fg: c.success, bg: "#E6F5EC", icon: "check" as IconName },
  }[tone];
  return (
    <div style={{ border: `1px solid ${map.fg}33`, background: map.bg, color: map.fg, borderRadius: 16, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
      <Icon name={map.icon} size={18} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 900 }}>{title}</div>
        {children && <div style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3, color: c.muted }}>{children}</div>}
      </div>
    </div>
  );
}

export function StatusPill({ children, tone = "neutral" }: { children: ReactNode; tone?: "success" | "warning" | "danger" | "info" | "neutral" }) {
  const s = creamTokens.status[tone];
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: s.bg, color: s.fg, borderRadius: 999, padding: "4px 9px", fontSize: 11, fontWeight: 800, lineHeight: 1.4 }}>{children}</span>;
}

export function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <div style={{ position: "relative", minWidth: 220 }}>
      <Icon name="search" size={16} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: c.muted }} />
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} style={{ width: "100%", padding: "10px 12px 10px 36px", border: `1.5px solid ${c.line}`, borderRadius: 12, fontSize: 13, fontFamily: creamTokens.font.body, outline: "none", background: c.surface, color: c.ink, boxSizing: "border-box" }} />
    </div>
  );
}

export function EmptyState({ icon = "clipboard-list", title, description }: { icon?: IconName; title: string; description?: string }) {
  return (
    <div style={{ display: "grid", placeItems: "center", padding: "46px 20px", textAlign: "center", color: c.muted }}>
      <div style={{ width: 56, height: 56, borderRadius: 20, background: c.roseSoft, color: c.rose, display: "grid", placeItems: "center", marginBottom: 14 }}><Icon name={icon} size={28} /></div>
      <div style={{ fontSize: 17, fontWeight: 900, color: c.ink }}>{title}</div>
      {description && <div style={{ marginTop: 7, maxWidth: 360, fontSize: 13, lineHeight: 1.55 }}>{description}</div>}
    </div>
  );
}

export function LoadingState({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {Array.from({ length: rows }, (_, index) => <div key={index} style={{ height: 88, borderRadius: 18, background: `linear-gradient(90deg, ${c.creamDeep}, ${c.roseSoft}, ${c.creamDeep})`, backgroundSize: "240% 100%", animation: "shimmer 1.4s infinite" }} />)}
    </div>
  );
}

export function ErrorState({ title = "Unable to load this workspace", description }: { title?: string; description?: string }) {
  return <AlertCard tone="danger" title={title}>{description || "Unable to reach the server. Check that the backend is running and try again."}</AlertCard>;
}

export function ActionButton({ icon, label, onClick, tone = "ghost", disabled = false }: { icon: IconName; label: string; onClick: () => void; tone?: "primary" | "ghost" | "danger"; disabled?: boolean }) {
  const styles = {
    primary: { background: c.rose, color: "#fff", border: "none" },
    ghost: { background: c.surface, color: c.chocolate, border: `1px solid ${c.line}` },
    danger: { background: c.critical, color: "#fff", border: "none" },
  }[tone];
  return (
    <button type="button" disabled={disabled} onClick={onClick} style={{ ...styles, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, borderRadius: 12, padding: "9px 12px", minHeight: 38, fontSize: 12, fontWeight: 800, fontFamily: creamTokens.font.body, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1 }}>
      <Icon name={icon} size={16} />
      {label}
    </button>
  );
}
